import * as APSConstants from './constants';

/**
 * Action notifying the intent to add feedback to an item
 *
 * @param {string} sourceId - id of the source the item belongs to
 * @param {string} itemPandoraId - pandoraId of the item to give feedback for
 * @param {Bool} isPositive - true for thumb up, false for thumb down
 * @returns {object} return an Action of type ADD_IEM_FEEDBACK
 */
export function addItemFeedback({ sourceId, itemPandoraId, isPositive }) {
  return {
    type: APSConstants.APSActions.ADD_ITEM_FEEDBACK,
    data: {
      sourceId,
      itemPandoraId,
      isPositive,
    },
  };
}

/**
 * Action notifying the intent of fetching the current APS item for a source
 *
 * @param {string} sourceId - the source id we want the item from
 * @param {Bool} forceActive - an optional flag indicating user wishes to continue playback after entering STREAM_INACTIVE state
 * @returns {object} return an Action of type PLAY_CURRENT_ITEM_FOR_SOURCE
 */
export function playCurrentItemForSource({ sourceId, forceActive }) {
  return {
    type: APSConstants.APSActions.PLAY_CURRENT_ITEM_FOR_SOURCE,
    data: {
      sourceId,
      forceActive,
    },
  };
}

/**
 * Action notifying that an APS-served item ended playing
 *
 * @param {string} sourceId - id of the source the item belongs to
 * @param {string} itemPandoraId - pandoraId of the item to give feedback for
 * @param {string} reason - the reason it ended, eg. ERROR, NORMAL
 * @param {float} elapsedTime - playback ended/stopped time
 * @returns {object} return an Action of type ITEM_ENDED
 */
export function itemEnded({ sourceId, itemPandoraId, reason, elapsedTime }) {
  return {
    type: APSConstants.APSActions.ITEM_ENDED,
    data: {
      sourceId,
      itemPandoraId,
      reason,
      elapsedTime,
    },
  };
}

/**
 * Action notifying the intent of pausing the current source
 *
 * @param root0
 * @param root0.onUnload
 * @returns {object} return an Action of type PAUSE_SOURCE
 */
export function pauseSource({ onUnload } = {}) {
  return {
    type: APSConstants.APSActions.PAUSE_SOURCE,
    data: {
      onUnload,
    },
  };
}

/**
 * Action notifying the intent to play a source
 *
 * @param {string} pandoraId - the source id
 * @param {Integer} index - an optional index to play the source from
 * @param {string} sortOrder - an optional order to play the source in (eg. ALPHA, MOST_RECENT_ADDED)
 * @param {string} repeat - an optional repeat mode to play the source in
 * @param {Bool} shuffle - an optional flag to start the source in shuffle mode
 * @param {string} context - context in which play originated
 * @returns {object} return an Action of type PLAY_SOURCE
 */
export function playSource({ pandoraId, index, sortOrder, repeat, shuffle, context }) {
  return {
    type: APSConstants.APSActions.PLAY_SOURCE,
    data: {
      sourceId: pandoraId,
      index,
      sortOrder,
      repeat,
      shuffle,
      context,
    },
  };
}

/**
 * Action notifying the intent to start playing a source from a specific itemId
 *
 * @param {string} id - the source id
 * @param {string} pandoraId - the pandora id
 * @param {Integer} index - an optional index to play the source from
 * @param {string} sortOrder - an optional order to play the source in (eg. ALPHA, MOST_RECENT_ADDED)
 * @param {string} repeat - an optional repeat mode to play the source in
 * @param {Bool} shuffle - an optional flag to start the source in shuffle mode
 * @param {string} context - context in which play originated
 * @returns {object} return an Action of type PLAY_SOURCE
 */
export function playSourceWithItemId({
  id,
  pandoraId,
  index,
  sortOrder,
  repeat,
  shuffle,
  context,
}) {
  return {
    type: APSConstants.APSActions.PLAY_SOURCE,
    data: {
      sourceId: id,
      itemId: pandoraId,
      index,
      sortOrder,
      repeat,
      shuffle,
      context,
    },
  };
}

/**
 * Action notifying the intent to fetch fresh data for the current item
 *
 * @returns {object} return an Action of type REFRESH_CURRENT_ITEM
 */
export function refreshCurrentItem() {
  return {
    type: APSConstants.APSActions.REFRESH_CURRENT_ITEM,
  };
}

/**
 * Action notifying the intent to reset APS session
 *
 * @returns {object} return an Action of type RESET_APS
 */
export function resetAPS() {
  return {
    type: APSConstants.APSActions.RESET_APS,
  };
}

/**
 * Action notifying the intent to reset current source without starting playback
 *
 * @returns {object} return an Action of type RESET_CURRENT_SOURCE
 */
export function resetCurrentSource() {
  return {
    type: APSConstants.APSActions.RESET_CURRENT_SOURCE,
  };
}

/**
 * Action notifying the intent to remove feedback from an item
 *
 * @param {string} sourceId - id of the source the item belongs to
 * @param {string} itemPandoraId - pandoraId of the item to remove feedback from
 * @param {Bool} isPositive - true for thumb up, false for thumb down
 * @returns {object} return an Action of type REMOVE_IEM_FEEDBACK
 */
export function removeItemFeedback({ sourceId, itemPandoraId, isPositive }) {
  return {
    type: APSConstants.APSActions.REMOVE_ITEM_FEEDBACK,
    data: {
      sourceId,
      itemPandoraId,
      isPositive,
    },
  };
}

/**
 * Action notifying the intent to change a source's repeat mode
 *
 * @param {string} sourceId - the source id
 * @param {string} mode - which mode we're setting
 * @returns {object} return an Action of type REPEAT_SOURCE
 */
export function repeatSource({ sourceId, mode }) {
  return {
    type: APSConstants.APSActions.REPEAT_SOURCE,
    data: {
      sourceId,
      mode,
    },
  };
}

/**
 * Action notifying the intent to replay an item
 *
 * @param {string} sourceId - the source id the item belongs to
 * @param {string} itemPandoraId - pandoraId of the item to be replayed
 * @returns {object} return an Action of type REPLAY_ITEM
 */
export function replayItem({ sourceId, itemPandoraId }) {
  return {
    type: APSConstants.APSActions.REPLAY_ITEM,
    data: {
      sourceId,
      itemPandoraId,
    },
  };
}

/**
 * Action notifying the intent to resume playing the current source
 *
 * @returns {object} return an Action of type RESUME_SOURCE
 */
export function resumeSource() {
  return {
    type: APSConstants.APSActions.RESUME_SOURCE,
  };
}

/**
 * Action notifying the reducer to set the current item
 *
 * @param {object} item - the item to make current
 */

export function setCurrentItem({ item }) {
  return {
    type: APSConstants.APSActions.SET_CURRENT_ITEM,
    data: {
      item,
    },
  };
}

/**
 * Action notifying the intent to turn a source's shuffle setting on or off
 *
 * @param {string} sourceId - the source id
 * @param {Bool} enabled - which mode we're setting
 * @returns {object} return an Action of type SHUFFLE_SOURCE
 */
export function shuffleSource({ sourceId, enabled }) {
  return {
    type: APSConstants.APSActions.SHUFFLE_SOURCE,
    data: {
      sourceId,
      enabled,
    },
  };
}

/**
 * Action notifying the intent to skip the current item
 *
 * @param {string} sourceId - the source id the item belongs to
 * @param {number} index - the index of the item we're skipping
 * @param {number} relativeIndex - positive for forward, negative for backward
 * @param {number} modePandoraId - mode id with a pandora type prepended to it
 * @returns {object} return an Action of type SKIP_ITEM
 */
export function skipItem({ sourceId, index, relativeIndex, modePandoraId }) {
  return {
    type: APSConstants.APSActions.SKIP_ITEM,
    data: {
      sourceId,
      index,
      relativeIndex,
      modePandoraId,
    },
  };
}

export function playAdItem({ item }) {
  return {
    type: APSConstants.APSActions.PLAY_AD_ITEM,
    data: {
      item,
    },
  };
}

export function logApsError({ apsEndpoint }) {
  return {
    type: APSConstants.APSActions.LOG_APS_ERROR,
    data: {
      apsEndpoint,
    },
  };
}
